/** Event detail — category & session-type chip filters + results table */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { rpc, RpcError } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ArrowLeft, Users, CloudSun, Timer } from 'lucide-react';
import { SessionTypeBadge } from './SessionTypeBadge';
import { WeatherCard } from '@/components/session/WeatherCard';
import { ResultsTable } from './ResultsTable';
import { PositionProgression } from './PositionProgression';
import { PaceGroups } from './PaceGroups';
import type { GetRaceEventResponse, GetRaceSessionResponse } from '@/types/telemetry';

const OVERVIEW_TYPE = 'overview';

interface EventDetailProps {
  championshipId: string;
  eventId: string;
  initialCategory?: string;
  initialType?: string;
  initialDetailTab?: string;
  highlightDriverId?: string;
}

export function EventDetail({ championshipId, eventId, initialCategory, initialType, initialDetailTab, highlightDriverId }: EventDetailProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const [eventData, setEventData] = useState<GetRaceEventResponse | null>(null);
  const [sessionData, setSessionData] = useState<GetRaceSessionResponse | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeSessionType, setActiveSessionType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Tabs below the results table (non-overview only)
  type DetailTab = 'weather' | 'pace';
  const validDetailTabs: DetailTab[] = ['weather', 'pace'];
  const [detailTab, setDetailTabState] = useState<DetailTab>(
    validDetailTabs.includes(initialDetailTab as DetailTab) ? (initialDetailTab as DetailTab) : 'weather'
  );

  // Pace group highlighting
  const [lockedDriverIds, setLockedDriverIds] = useState<Set<string>>(new Set());
  const paceRef = useRef<HTMLDivElement>(null);

  const activeHighlightIds = useMemo(() => {
    return lockedDriverIds;
  }, [lockedDriverIds]);

  // Click-away handler
  useEffect(() => {
    if (lockedDriverIds.size === 0) return;
    function handleClickOutside(e: MouseEvent) {
      if (paceRef.current && !paceRef.current.contains(e.target as Node)) {
        setLockedDriverIds(new Set());
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [lockedDriverIds]);

  const handlePaceClick = useCallback((ids: Set<string>) => {
    setLockedDriverIds(prev => {
      const prevArr = Array.from(prev);
      if (prevArr.length === ids.size && prevArr.every(id => ids.has(id))) return new Set();
      return ids;
    });
  }, []);

  const SESSION_TYPE_ORDER = ['qualifying', 'heat-1', 'heat-2', 'heat-3', 'final'];
  const CATEGORY_ORDER = ['Micro', 'Mini', 'Junior', 'Club', 'Max', 'Master', 'DD2', 'DD2 Master'];

  const sortCategories = (cats: string[]) =>
    [...cats].sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      // Known categories first in defined order, unknown at end alphabetically
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a.localeCompare(b);
    });

  // Build the URL with query params for current selection
  const buildUrl = useCallback((cat: string | null, type: string | null, driver?: string | null, detail?: string | null) => {
    const base = cat ? `#/races/${championshipId}/${eventId}/${encodeURIComponent(cat)}` : `#/races/${championshipId}/${eventId}`;
    const qp = new URLSearchParams();
    if (type) qp.set('type', type);
    if (driver) qp.set('driver', driver);
    if (detail && detail !== 'weather') qp.set('detail', detail);
    const qs = qp.toString();
    return qs ? `${base}?${qs}` : base;
  }, [championshipId, eventId]);

  // Wrap setDetailTab to also update the URL
  const setDetailTab = useCallback((tab: DetailTab) => {
    setDetailTabState(tab);
    window.history.replaceState(null, '', buildUrl(activeCategory, activeSessionType, highlightDriverId, tab));
  }, [buildUrl, activeCategory, activeSessionType, highlightDriverId]);

  // Load event data
  const loadEvent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rpc<GetRaceEventResponse>('GetRaceEvent', { event_id: eventId });
      setEventData(res);
      // Use URL params if available, otherwise default to overview
      if (res.sessions?.length > 0) {
        const cat = initialCategory || null;
        setActiveCategory(cat);
        const typesForCat = [...new Set(
          res.sessions.filter(s => s.categoryName === cat).map(s => s.sessionType)
        )].sort((a, b) => SESSION_TYPE_ORDER.indexOf(a) - SESSION_TYPE_ORDER.indexOf(b));
        // Default to overview unless URL explicitly specifies a session type
        const type = initialType ? (typesForCat.includes(initialType) ? initialType : OVERVIEW_TYPE) : OVERVIEW_TYPE;
        setActiveSessionType(type);
      }
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? t('races.noData') : (e.message || t('races.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);



  // Derived: session types for active category
  const sessionTypes = useMemo(() => {
    if (!eventData?.sessions || !activeCategory) return [];
    return [...new Set(
      eventData.sessions
        .filter(s => s.categoryName === activeCategory)
        .map(s => s.sessionType)
    )].sort((a, b) => SESSION_TYPE_ORDER.indexOf(a) - SESSION_TYPE_ORDER.indexOf(b));
  }, [eventData, activeCategory]);

  const isOverview = activeSessionType === OVERVIEW_TYPE;

  // Resolve the active session from category + session type
  const activeSessionId = useMemo(() => {
    if (!eventData?.sessions || !activeCategory || !activeSessionType || isOverview) return null;
    const match = eventData.sessions.find(
      s => s.categoryName === activeCategory && s.sessionType === activeSessionType
    );
    return match?.id || null;
  }, [eventData, activeCategory, activeSessionType, isOverview]);



  // When session type changes, update URL (keep driver and detail tab sticky)
  const handleSessionTypeChange = useCallback((type: string) => {
    setActiveSessionType(type);
    setHoveredDriverIds(new Set());
    setLockedDriverIds(new Set());
    window.history.replaceState(null, '', buildUrl(activeCategory, type, highlightDriverId, detailTab));
  }, [activeCategory, highlightDriverId, detailTab, buildUrl]);

  // Load session results when active session changes
  const loadSession = useCallback(async (sessionId: string) => {
    setSessionLoading(true);
    try {
      const res = await rpc<GetRaceSessionResponse>('GetRaceSession', { session_id: sessionId });
      setSessionData(res);
    } catch (e: any) {
      setSessionData(null);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  useEffect(() => {
    if (activeSessionId) loadSession(activeSessionId);
    else setSessionData(null);
  }, [activeSessionId, loadSession]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!eventData) return <ErrorState message={t('races.noData')} />;

  const { event, sessions, weather } = eventData;

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate(`#/races/${championshipId}/${eventId}`)}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              {event.trackName} {activeCategory ? `— ${activeCategory}` : ''}
            </h1>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-text-muted">
              <span>{t('races.round', { n: event.roundNumber })}</span>
              <span>·</span>
              <span>{formatDate(event.dateStart)}</span>
              <span>·</span>
              <span>{event.country}</span>
            </div>
          </div>
        </div>
      </div>



      {/* Session type chips (row 2) — Overview + individual session types */}
      {sessionTypes.length > 0 && (
        <div className="flex items-center gap-1 mb-6">
          {/* Overview chip */}
          <button
            onClick={() => handleSessionTypeChange(OVERVIEW_TYPE)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              isOverview
                ? 'text-accent bg-accent/10'
                : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            {t('races.overview')}
          </button>
          {sessionTypes.map(type => {
            const isActive = type === activeSessionType;
            return (
              <button
                key={type}
                onClick={() => handleSessionTypeChange(type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                  isActive
                    ? 'text-accent bg-accent/10'
                    : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                {t(`races.sessionType.${type}` as any)}
              </button>
            );
          })}
        </div>
      )}

      {/* Overview: position progression */}
      {isOverview && activeCategory && (
        <PositionProgression
          eventId={eventId}
          categoryName={activeCategory}
          highlightDriverId={highlightDriverId}
        />
      )}

      {/* Individual session view */}
      {!isOverview && (
        <>
          {/* Session info bar */}
          {sessionData?.session && (
            <div className="flex items-center gap-4 mb-4 text-xs text-text-muted">
              <span className="flex items-center gap-1.5">
                <Users size={12} />
                {sessionData.session.entryCount} {t('races.entries').toLowerCase()}
              </span>
              {sessionData.session.totalLaps > 0 && (
                <span>{sessionData.session.totalLaps} {t('races.laps').toLowerCase()}</span>
              )}
            </div>
          )}

          {/* Results */}
          {sessionLoading ? (
            <LoadingSpinner />
          ) : sessionData?.entries ? (
            <>
              <div className="card p-0 overflow-hidden">
                <ResultsTable
                  entries={sessionData.entries}
                  compact
                  showLaps={activeSessionType !== 'qualifying'}
                  highlightDriverId={highlightDriverId}
                  highlightDriverIds={activeHighlightIds}
                  onDriverClick={(driverId) => navigate(`#/races/driver/${driverId}`)}
                />
              </div>

              {/* Detail tabs below the results table */}
              <div className="mt-8">
                <div className="flex items-center gap-1 mb-6">
                  <button
                    onClick={() => setDetailTab('weather')}
                    className={`
                      flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                      ${detailTab === 'weather' ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
                    `}
                  >
                    <CloudSun size={13} />
                    {t('races.sessionTabWeather')}
                  </button>
                  <button
                    onClick={() => setDetailTab('pace')}
                    className={`
                      flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer
                      ${detailTab === 'pace' ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'}
                    `}
                  >
                    <Timer size={13} />
                    {t('races.sessionTabPace')}
                  </button>
                </div>

                {detailTab === 'weather' && (
                  <WeatherCard weather={sessionData.weather || { condition: '' }} />
                )}

                {detailTab === 'pace' && (
                  <div ref={paceRef}>
                    <PaceGroups
                      entries={sessionData.entries}
                      activeDriverIds={activeHighlightIds}
                      onClick={handlePaceClick}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-text-muted text-center py-12">{t('races.noData')}</p>
          )}
        </>
      )}
    </div>
  );
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '–';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}
